import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { apiGet, apiPost } from '../api/client';
import { spacing, fontSize, fontWeight } from '../utils/theme';

interface GiftAlertItem {
  readonly id: string;
  readonly kind: 'credit' | 'voucher';
  readonly subtype: 'gift' | 'promo';
  readonly ownerName: string;
  readonly label: string;
  readonly receivedAt: string;
  readonly needsClaim: boolean;
  readonly code: string | null;
}
interface GiftAlertsResponse {
  readonly items: GiftAlertItem[];
  readonly unseenCount: number;
  readonly latestAt: string | null;
}

/**
 * GiftBanner — high-visibility, dismissible banner shown to a customer when a
 * gift or promo has arrived since they last dismissed it. Mounted globally in
 * UserShell so it appears across all customer screens. Dismiss acks the backend
 * marker (users.gift_alerts_seen_at) so it stays gone until something newer
 * arrives — durable + cross-device.
 */
export default function GiftBanner() {
  const router = useRouter();
  const [data, setData] = useState<GiftAlertsResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchAlerts = useCallback(() => {
    apiGet<GiftAlertsResponse>('/api/me/gift-alerts')
      .then((d) => { setData(d); setDismissed(false); })
      .catch(() => { /* non-critical: banner hidden if fetch fails */ });
  }, []);

  useFocusEffect(useCallback(() => { fetchAlerts(); }, [fetchAlerts]));

  if (dismissed || !data || data.unseenCount === 0) return null;

  const first = data.items[0];
  const multiple = data.unseenCount > 1;
  const headline = multiple ? `You have ${data.unseenCount} new gifts & promos` : first.label;
  // With multiple alerts we always route to Rewards, so the copy must match —
  // and since the backend auto-binds email-matched gifts, they're actually
  // visible there. "Tap to claim" only for a single still-unbound item.
  const sub = !multiple && first?.needsClaim ? 'Tap to claim it' : 'Tap to view in Rewards';

  // Gifts are auto-deposited by the backend's bind sweep (needsClaim=false) →
  // Rewards. A rare still-pending one deep-links into its claim flow.
  const goToRewards = () => {
    let target = '/user/rewards';
    if (!multiple && first?.needsClaim) {
      target = first.kind === 'credit' ? '/redeem/credit' : first.code ? `/redeem/${first.code}` : '/user/rewards';
    }
    router.push(target as never);
  };
  const handleDismiss = () => {
    setDismissed(true);
    apiPost('/api/me/gift-alerts/ack').catch(() => { /* best-effort */ });
  };

  return (
    <View style={styles.banner}>
      <TouchableOpacity style={styles.main} onPress={goToRewards} activeOpacity={0.85} accessibilityRole="button">
        <View style={styles.iconWrap}>
          <Ionicons name="gift" size={20} color="#ffffff" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headline} numberOfLines={1}>{headline}</Text>
          <Text style={styles.sub} numberOfLines={1}>{sub}</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color="rgba(255,255,255,0.85)" />
      </TouchableOpacity>
      <TouchableOpacity onPress={handleDismiss} hitSlop={10} style={styles.close} accessibilityLabel="Dismiss gift banner">
        <Ionicons name="close" size={20} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#245f73',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  main: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  iconWrap: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  headline: { color: '#ffffff', fontSize: fontSize.sm, fontWeight: fontWeight.bold },
  sub: { color: 'rgba(255,255,255,0.85)', fontSize: fontSize.xs, marginTop: 1 },
  close: { padding: 4 },
});
